import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { base44 } from "@/api/base44Client";
import { getVisitorProfile, rememberHearts } from "@/lib/visitorProfile";
import { withNetworkRetry } from "@/lib/requestRetry";

const DEFAULT = {
  countFor: (_id) => 0, hasHeart: (_id) => false,
  toggleHeart: async (_id) => {}, pendingFor: (_id) => false,
  errorFor: (_id) => "", ready: false,
};
const CommunityHeartContext = createContext(DEFAULT);
const unpack = (response) => response?.data || response || {};

/**
 * Real community endorsements, not visits or scores. Any visitor can endorse
 * a published place without registering. One active heart per 60-day browser
 * identity is counted server-side. A failure never pretends the vote was saved.
 *
 * The app's older purely local heart selections cannot safely be counted as
 * public endorsements. Shared counts now come only from persisted votes.
 */
export function CommunityHeartProvider({ children }) {
  const [counts, setCounts] = useState({});
  const [mine, setMine] = useState(() => new Set());
  const [pending, setPending] = useState(() => new Set());
  const [errors, setErrors] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    const viewer = getVisitorProfile().key;
    withNetworkRetry(() => base44.functions.invoke("community-hearts", {action: "summary", visitor_key: viewer}))
      .then((result) => {
        if (!active) return;
        const data = unpack(result);
        if (!data.ok || !data.counts) throw new Error("Counts unavailable");
        setCounts(data.counts);
        const selections = new Set((data.mine || []).map(String));
        setMine(selections);
        rememberHearts([...selections]);
        setReady(true);
      })
      .catch(() => { if (active) setReady(false); });
    return () => { active = false; };
  }, []);

  const countFor = useCallback((id) => Number(counts[String(id)] || 0), [counts]);
  const hasHeart = useCallback((id) => mine.has(String(id)), [mine]);
  const pendingFor = useCallback((id) => pending.has(String(id)), [pending]);
  const errorFor = useCallback((id) => errors[String(id)] || "", [errors]);

  const toggleHeart = useCallback(async (id) => {
    if (!id || !ready) return;
    const key = String(id);
    if (pending.has(key)) return;
    const hearted = !mine.has(key);
    setPending((previous) => new Set(previous).add(key));
    setErrors((previous) => ({...previous, [key]: ""}));
    try {
      const result = unpack(await base44.functions.invoke("community-hearts", {
        action: "set", listing_id: key, hearted, visitor_key: getVisitorProfile().key,
      }));
      if (!result.ok || result.listing_id !== key) throw new Error(result.error || "Heart was not saved");
      setCounts((previous) => ({...previous, [key]: Number(result.count) || 0}));
      setMine((previous) => {
        const next = new Set(previous);
        if (result.hearted) next.add(key); else next.delete(key);
        rememberHearts([...next]);
        return next;
      });
    } catch {
      setErrors((previous) => ({...previous, [key]: "unavailable"}));
    } finally {
      setPending((previous) => {
        const next = new Set(previous);
        next.delete(key);
        return next;
      });
    }
  }, [ready, pending, mine]);

  const value = useMemo(() => ({
    countFor, hasHeart, toggleHeart, pendingFor, errorFor, ready,
  }), [countFor, hasHeart, toggleHeart, pendingFor, errorFor, ready]);

  return <CommunityHeartContext.Provider value={value}>{children}</CommunityHeartContext.Provider>;
}
export const useCommunityHearts = () => useContext(CommunityHeartContext);